import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MessageCircle, Check } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useT } from "@/i18n_extra2";
import { CITIES } from "@/data/cities";
import { whatsappUrl } from "@/utils";

export default function CityPage({ slug }) {
  const navigate = useNavigate();
  const t = useT();
  const city = CITIES.find((c) => c.slug === slug);

  useEffect(() => {
    if (city) {
      document.title = t.cityPage.metaTitle.replace("{city}", city.name);
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.content = t.cityExtra.metaDesc
        .replaceAll("{city}", city.name)
        .replaceAll("{drive}", city.drive === 0 ? 2 : city.drive);
      return () => {
        document.title = "ClearRoute Auto Glass — Windshield Repair & Replacement | Peabody, MA · Greater Boston";
      };
    }
  }, [city, t]);

  if (!city) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center gap-6 text-black/60">
        <p>404 — city not found</p>
        <Link to="/" className="text-[#D4AF37] hover:underline" data-testid="city-notfound-home-link">Back to home</Link>
      </div>
    );
  }

  const intro = city.drive === 0
    ? t.cityPage.introHomebase
    : t.cityPage.intro.replaceAll("{drive}", city.drive).replaceAll("{city}", city.name);

  const goQuote = () => navigate("/", { state: { scrollTo: "#quote" } });
  const others = CITIES.filter((c) => c.slug !== slug);

  return (
    <div className="App bg-[#FAF9F6] text-[#111111]">
      <div className="grain-overlay" />
      <Navbar />
      <main className="pt-40 pb-24 max-w-7xl mx-auto px-5 sm:px-8" data-testid="city-page">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-black/50 hover:text-[#B8860B] transition-colors" data-testid="city-back-link">
          <ArrowLeft className="w-4 h-4" /> {t.cityPage.back}
        </Link>

        <div className="mt-10 flex items-center gap-3">
          <span className="h-px w-10 bg-[#D4AF37]" />
          <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#D4AF37]">{t.cityPage.eyebrow}</span>
        </div>

        <h1 className="mt-6 font-display font-bold tracking-tighter leading-[0.98] text-4xl sm:text-6xl max-w-4xl">
          <span className="block overflow-hidden pb-2 -mb-2">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              {t.cityPage.h1a.replace("{city}", city.name)}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2 -mb-2 text-[#D4AF37]">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}>
              {t.cityPage.h1b}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="mt-8 max-w-2xl text-black/70 text-base sm:text-lg leading-relaxed"
          data-testid="city-intro"
        >
          {intro}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.45 }}
          className="mt-4 max-w-2xl text-black/50 text-sm sm:text-base leading-relaxed"
          data-testid="city-local-note"
        >
          {t.cityExtra.landmarkPhrase.replace("{landmark}", city.landmark)}
          {city.corridor !== "home" ? " " + t.cityExtra.corridors[city.corridor].replaceAll("{city}", city.name) : ""}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            onClick={goQuote}
            data-testid="city-quote-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-bold px-8 py-4 rounded-full transition-colors duration-300 hover:scale-105 transform"
          >
            {t.cityPage.ctaQuote} <ArrowUpRight className="w-5 h-5" />
          </button>
          <a
            href={whatsappUrl(t.whatsapp.prefill)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="city-whatsapp-cta"
            className="inline-flex items-center gap-2 border border-black/25 hover:border-[#25D366] hover:text-[#25D366] text-black font-semibold px-8 py-4 rounded-full transition-colors duration-300"
          >
            <MessageCircle className="w-5 h-5" /> {t.cityPage.ctaWhats}
          </a>
        </motion.div>

        <h2 className="mt-24 font-display font-bold tracking-tight text-2xl sm:text-3xl">
          {t.cityPage.servicesTitle.replace("{city}", city.name)}
        </h2>
        <div className="mt-8 border-t border-black/10">
          {t.services.items.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="border-b border-black/10 py-7 grid sm:grid-cols-[auto_1fr] gap-4 sm:gap-8 items-start"
              data-testid={`city-service-${i + 1}`}
            >
              <span className="font-display font-bold text-3xl text-outline-white leading-none">{String(i + 1).padStart(2, "0")}</span>
              <span>
                <span className="flex items-center gap-2.5 font-display font-semibold text-lg sm:text-xl">
                  <Check className="w-4 h-4 text-[#D4AF37]" /> {s.title}
                </span>
                <span className="block mt-2 text-sm text-black/55 leading-relaxed max-w-2xl">{s.desc}</span>
              </span>
            </motion.div>
          ))}
        </div>

        <h3 className="mt-20 text-xs font-bold tracking-[0.25em] uppercase text-black/40">{t.cityPage.otherTitle}</h3>
        <div className="mt-6 flex flex-wrap gap-3" data-testid="city-other-cities">
          {others.map((c) => (
            <Link
              key={c.slug}
              to={`/windshield-replacement-${c.slug}`}
              data-testid={`city-link-${c.slug}`}
              className="px-4 py-2 rounded-full border border-black/10 text-sm text-black/55 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
